import { useCallback, useRef, useState } from 'react';

interface UndoableAction {
    undo: () => void | Promise<void>;
    redo: () => void | Promise<void>;
}

interface UndoManagerState {
    past: UndoableAction[];
    future: UndoableAction[];
}

interface UndoManager {
    add: (action: UndoableAction) => void;
    undo: () => Promise<void>;
    redo: () => Promise<void>;
    canUndo: boolean;
    canRedo: boolean;
    clear: () => void;
}

export const useUndoManager = (): UndoManager => {
    const [state, setState] = useState<UndoManagerState>({ past: [], future: [] });
    const stateRef = useRef(state);
    const isExecutingRef = useRef(false);
    stateRef.current = state;

    // Add a new action to the undo stack
    const add = useCallback((action: UndoableAction) => {
        setState((prevState) => ({
            past: [...prevState.past, action],
            future: [], // Clear the redo stack when a new action is added
        }));
    }, []);

    // Undo the last action
    const undo = useCallback(async () => {
        // Ignore if already executing an operation
        if (isExecutingRef.current) {
            return;
        }

        const { past, future } = stateRef.current;

        if (past.length === 0) {
            return;
        }

        const newPast = [...past];
        const action = newPast.pop();

        if (action) {
            isExecutingRef.current = true;

            try {
                await action.undo();

                setState({
                    past: newPast,
                    future: [action, ...future],
                });
            } finally {
                isExecutingRef.current = false;
            }
        }
    }, []);

    // Redo the previously undone action
    const redo = useCallback(async () => {
        // Ignore if already executing an operation
        if (isExecutingRef.current) {
            return;
        }

        const { past, future } = stateRef.current;

        if (future.length === 0) {
            return;
        }

        const newFuture = [...future];
        const action = newFuture.shift();

        if (action) {
            isExecutingRef.current = true;

            try {
                await action.redo();

                setState({
                    past: [...past, action],
                    future: newFuture,
                });
            } finally {
                isExecutingRef.current = false;
            }
        }
    }, []);

    // Clear all undo/redo history
    const clear = useCallback(() => {
        setState({ past: [], future: [] });
    }, []);

    return {
        add,
        undo,
        redo,
        clear,
        canUndo: state.past.length > 0,
        canRedo: state.future.length > 0,
    };
};
